import { SvgIcon, Typography, Alert, Stack, Box, useTheme } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { Button } from 'src/components/button';
import { getTheme } from 'src/utils/get-theme';

import { ReactComponent as IconClock } from '../../../../assets/svg/clock.svg';
import { HousekeepingInspection } from '../../api';
import { usePostInspection } from '../../hooks/use-post-inspection';
import { InspectionAction } from '../enum/inspection-action';
import { useGetInspections } from '../../hooks/use-get-inspections';
import { useFormatInspectionDates } from '../../hooks/use-format-inspection-dates';

import { HousekeepingPreferencesModal } from './housekeeping-preferences-modal';
import { HousekeepingCard } from './housekeeping-card';

interface Props {
    className?: string;
    inspection: HousekeepingInspection;
    academicYear: string;
}

export const UpcomingHousekeeping: FC<Props> = ({ className, inspection, academicYear }) => {
    const theme = useTheme();
    const { isHosTheme } = getTheme();

    const { refetch } = useGetInspections(academicYear);
    const { t } = useTranslation();
    const { date, day, month, formattedOptInDeadline } = useFormatInspectionDates(inspection);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const { isLoading: isOptingOut, mutate: optOut } = usePostInspection(InspectionAction.OPT_OUT, {
        onSuccess: () => refetch(),
    });

    return (
        <HousekeepingCard
            className={className}
            statusBadge={
                <Alert
                    icon={false}
                    severity="success"
                    css={{
                        textTransform: 'uppercase',
                        backgroundColor: theme.palette.grey[isHosTheme ? 50 : 100],
                        color: theme.palette.grey[600],
                        border: isHosTheme ? `1px solid ${theme.palette.grey[100]}` : 'inherit',
                        borderRadius: 0,
                    }}
                >
                    {t('housekeeping_upcoming_badge')}
                </Alert>
            }
            inspection={inspection}
            title={`${day}, ${month} ${date}`}
            subtitle={t('housekeeping_upcoming_subtitle')}
            additionalInfo={
                <Box>
                    <Alert
                        css={{
                            padding: '8px',
                            border: '1px solid #FFEDD5',
                            backgroundColor: '#FFF7ED',
                            borderRadius: 0,
                        }}
                        color="warning"
                        icon={
                            <SvgIcon fontSize="inherit" css={{ paddingRight: '6px' }}>
                                <IconClock css={{ fill: '#EA580C' }} />
                            </SvgIcon>
                        }
                    >
                        <Typography variant="subtitle1" color="#EA580C">
                            <Trans
                                i18nKey="housekeeping_opt_in_deadline"
                                values={{
                                    date: formattedOptInDeadline,
                                }}
                            />
                        </Typography>
                    </Alert>
                </Box>
            }
        >
            <Stack direction="row" gap="16px" justifyContent="space-between">
                <Button
                    loading={isOptingOut}
                    fullWidth
                    onClick={() => optOut({ id: inspection.id, academicYear })}
                    css={{
                        border: `1px solid ${theme.palette.grey[200]}`,
                        textTransform: 'uppercase',
                    }}
                >
                    {t('housekeeping_upcoming_action_skip')}
                </Button>
                <Button
                    fullWidth
                    surrounding="light"
                    css={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textTransform: 'uppercase',
                    }}
                    onClick={() => setIsModalOpen(true)}
                >
                    {t('housekeeping_upcoming_action_opt_in')}
                </Button>
            </Stack>
            <HousekeepingPreferencesModal
                open={isModalOpen}
                maintenanceId={inspection.id}
                academicYear={academicYear}
                unitType={inspection.unit_type}
                onClose={(reloadHousekeeping: boolean) => {
                    setIsModalOpen(false);

                    if (reloadHousekeeping) {
                        refetch();
                    }
                }}
            />
        </HousekeepingCard>
    );
};
